import React, { useState, useEffect } from "react";
import { Accordion, AccordionItem as Item } from "@szhsin/react-accordion";
import {
	FaPlayCircle,
	FaChevronUp,
	FaFilePdf,
	FaEye,
	FaExternalLinkSquareAlt,
	FaLink,
	FaInfoCircle,
	FaFileAlt,
	FaImage,
	FaGlobe,
	FaDatabase,
	FaClipboardList,
	FaChartArea,
	FaArrowCircleRight,
	FaQuestionCircle,
} from "react-icons/fa";
import { VideoPopup } from "./common/VideoPopup";
import { HelpPopup } from "./HelpPopup";
import { setHelpAccessed, logActivity } from "../services/dashApiService";
import { TransitionSlideUp } from "./transitions/TransitionSlideUp";
import { TransitionConfig } from "./../configs/transitionConfig";
import { isXamarin } from "../services/mobileService";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { BrowserPopup } from "./BrowserPopup";

const Icons = {
	FaPlayCircle,
	FaChevronUp,
	FaFilePdf,
	FaEye,
	FaExternalLinkSquareAlt,
	FaLink,
	FaInfoCircle,
	FaFileAlt,
	FaImage,
	FaGlobe,
	FaDatabase,
	FaClipboardList,
	FaChartArea,
	FaArrowCircleRight,
	FaQuestionCircle,
};

const AccordionItem = ({ header, ...rest }) => (
	<Item
		{...rest}
		header={({ state: { isEnter } }) => (
			<>
				<div className={`${isEnter ? "text-blue-900" : "text-blue-700"}`}>
					{header}
				</div>
				<div
					className={`ml-auto mt-2 transition-transform duration-200 ease-out ${
						isEnter ? "text-blue-900 rotate-180" : "text-blue-700"
					}`}
					style={{ transform: isEnter ? "rotate(0deg)" : "rotate(180deg)" }}
				>
					<FaChevronUp />
				</div>
			</>
		)}
		className=""
		buttonProps={{
			className: ({ isEnter }) =>
				`flex w-full p-4 text-left  mt-2 ${
					isEnter
						? "border border-white rounded-xl bg-gradient-to-t from-gray-100 to-gray-50"
						: "border border-white rounded-xl bg-gradient-to-b from-gray-100 to-gray-50"
				}`,
		}}
		contentProps={{
			className: "transition-height duration-200 ease-out",
		}}
		panelProps={{ className: "pt-1 pb-1 px-0 md:px-4" }}
	/>
);

export const Links = ({ links }) => {
	const [items, setItems] = useState();
	const [groups, setGroups] = useState();
	const [groupIcons, setGroupIcons] = useState();
	const [warnings, setWarnings] = useState({});
	const [isShowingVideo, setIsShowingVideo] = useState(false);
	const [isShowingHelp, setIsShowingHelp] = useState(false);
	const [isShowing, setIsShowing] = useState(false);
	const [copied, setCopied] = useState(false);
	const [isMobile] = useState(isXamarin());
	const [timer, setTimer] = useState();
	const [browser, setBrowser] = useState(false);

	const closeBrowserPopup = () => {
		setBrowser(false);
	};

	const handleFinishLoading = () => {
		setTimeout(() => {
			setIsShowing(true);
		}, TransitionConfig.slideYTransition.enterDuration);
	};

	useEffect(() => {
		handleFinishLoading();
	}, []);

	useEffect(() => {
		if (!copied) {
			return;
		}

		if (timer) {
			clearTimeout(timer);
		}

		const _timer = setTimeout(() => {
			setCopied(false);
			setTimer();
		}, 1000 * 1);

		setTimer(_timer);

		return () => {
			if (_timer) {
				clearTimeout(_timer);
			}
		};
	}, [copied]);

	useEffect(() => {
		if (!links || links.length === 0) {
			return;
		}

		const items = {};
		const groups = [];
		const warnings = {};
		const groupIcons = {};

		for (const it of links) {
			const group = it.def.group;
			if (!items[group]) {
				groups.push(group);
				items[group] = [];
				warnings[group] = [];
			}

			items[group].push(it);
			groupIcons[group] = groupIcons[group] || it.def.icon;

			if (it.def.warning && warnings[group].indexOf(it.def.warning) < 0) {
				warnings[group].push(it.def.warning);
			}
		}

		setGroups(groups);
		setGroupIcons(groupIcons);
		setItems(items);
		setWarnings(warnings);
	}, [links]);

	const playHandler = (id, group) => {
		const item = items[group].find((it) => it.id === id);
		const type = (item.type || "link").toLowerCase();

		switch (type) {
			case "text":
				setIsShowingHelp(item);
				onWatched(item.id, true);
				break;

			case "video":
				setIsShowingVideo(item);
				break;

			default:
			case "link":
				if (item.def.popup) {
					setBrowser({ title: item.name, url: item.url });
				} else if (isMobile) {
					setCopied(item.id);
				} else {
					window.open(item.url, "_blank");
				}

				onWatched(item.id);

				logActivity("link", "app - links", {
					type,
					timestamp: Date.now(),
					itemId: item.id,
					isMobile,
					src: "Useful Links",
				});
				break;
		}
	};

	const onWatched = (id, localOnly) => {
		const _items = { ...items };
		let found = false;

		for (const key in _items) {
			for (const it of _items[key]) {
				if (it.id.toLowerCase() === id.toLowerCase()) {
					it.lastAccessed = Date.now();
					it.accessed = (it.accessed || 0) + 1;
					found = true;
					break;
				}
			}

			if (found) {
				break;
			}
		}

		setItems(_items);

		// update the back end
		if (!localOnly) {
			setHelpAccessed(id)
				.then(() => {})
				.catch((error) => {
					console.error(error);
				});
		}
	};

	if (!items) {
		return <>No links to display</>;
	}

	const renderWarnings = (group) => {
		return warnings[group].map((w, index) => {
			return (
				<p
					key={`warning_${group}_${index}`}
					className={`${
						index < warnings[group].length - 1 ? "mb-1" : "mb-5"
					} mt-1 text-black text-sm px-2`}
					dangerouslySetInnerHTML={{ __html: `${w}` }}
				></p>
			);
		});
	};

	const renderItems = (items) => {
		return items.map((item) => {
			let visitText = `Click to ${item.def.visitText || "visit"}`;
			let visitColour = "bg-blue-800 border-blue-400 text-white";
			if (isMobile && item.type === "Link" && !item.def.popup) {
				if (copied === item.id) {
					visitText = "Copied";
					visitColour = "bg-blue-200 text-gray-800 border-blue-400";
				} else {
					visitText = "Copy url";
				}
			}

			return (
				<div
					key={`ai_item_${item.group}_${item.code}`}
					className={`max-ht-24 ht-24 rounded-md border border-gray-200 grid grid-cols-12 auto-rows-fr gap-2 p-2 mb-2 shadow-md shadow-gray-400/50`}
				>
					<div className="col-span-9 md:col-span-8 flex flex-1 flex-col items-stretch justify-center h-full">
						<div
							className="text-sm md:text-md text-gray-900 font-bold"
							dangerouslySetInnerHTML={{ __html: `${item.name}` }}
						></div>
						<div
							className="text-sm py-1 text-gray-600"
							dangerouslySetInnerHTML={{ __html: item.description }}
						></div>
						<div className="text-xs width-full italic text-gray-600">
							{item.lastAccessed
								? `Viewed ${new Date(item.lastAccessed).toLocaleDateString()}`
								: "Unviewed"}
						</div>
					</div>
					{isMobile ? (
						<CopyToClipboard text={item.url}>
							<div
								className={`col-span-3 md:col-span-4 flex-1 cursor-pointer h-full`}
								onClick={() => {
									playHandler(item.id, item.def.group);
								}}
							>
								<div
									className={`w-full h-3/4`}
									style={{
										backgroundImage: `url('${item.thumbnail}')`,
										backgroundPosition: "center",
										backgroundSize: item.def.thumbSize || "cover",
										backgroundRepeat: "no-repeat",
									}}
								></div>
								<div className={`w-full h-1/4 text-center text-xs`}>
									<div
										className={`border rounded relative bottom-0 mt-0.5 ${visitColour}`}
									>
										{visitText}
									</div>
								</div>
							</div>
						</CopyToClipboard>
					) : (
						<div
							className={`col-span-3 md:col-span-4 flex-1 cursor-pointer h-full`}
							onClick={() => {
								playHandler(item.id, item.def.group);
							}}
						>
							<div
								className={`w-full h-3/4`}
								style={{
									backgroundImage: `url('${item.thumbnail}')`,
									backgroundPosition: "center",
									backgroundSize: item.def.thumbSize || "cover",
									backgroundRepeat: "no-repeat",
								}}
							></div>
							<div className={`w-full h-1/4 text-center text-xs`}>
								<div
									className={`border rounded border-blue-400 relative bottom-0 mt-0.5 ${
										copied === item.id ? "bg-orange-800" : "bg-blue-800"
									} text-white`}
								>
									{visitText}
								</div>
							</div>
						</div>
					)}
				</div>
			);
		});
	};

	const rendergroups = () => {
		return groups.map((group) => {
			let total = 0;
			let viewed = 0;

			for (const item of items[group]) {
				total += 1;
				if (item.lastAccessed) {
					viewed += 1;
				}
			}

			let text = "all viewed";
			if (total > viewed) {
				text = total - viewed + " unviewed";
			}

			const Icon = Icons[groupIcons[group]] || Icons.FaGlobe;

			return (
				<AccordionItem
					key={`ai_group_${group}`}
					header={
						<span>
							<span>
								<Icon className="inline -top-0.5 relative" />
							</span>{" "}
							<span className=" text-base font-bold">{group} - </span>
							<span className="text-sm">{text}</span>
						</span>
					}
				>
					{renderWarnings(group)}
					{renderItems(items[group])}
				</AccordionItem>
			);
		});
	};

	return (
		<TransitionSlideUp
			show={isShowing}
			enterTime={TransitionConfig.slideYTransition.enterDuration}
			leaveTime={TransitionConfig.slideYTransition.leaveDuration}
		>
			<div className="w-full max-w-panel min-h-24 mx-auto">
				<Accordion transition transitionTimeout={200}>
					{rendergroups()}
				</Accordion>
			</div>
			<VideoPopup
				show={isShowingVideo ? true : false}
				onClose={() => setIsShowingVideo(false)}
				onWatched={() => {
					onWatched(isShowingVideo.id);
				}}
				title={`${isShowingVideo.name}`}
				videoUrl={`${isShowingVideo.url}`}
				registration={false}
			/>
			<HelpPopup
				show={isShowingHelp ? true : false}
				helpCode={isShowingHelp.code || null}
				onClose={() => setIsShowingHelp(false)}
				registration={false}
			/>
			<BrowserPopup
				title={browser?.text}
				url={browser?.url}
				show={browser ? true : false}
				onClose={closeBrowserPopup}
			/>
		</TransitionSlideUp>
	);
};
